import type { ClientMessage, ServerMessage } from "@golazo/shared";
import { migrateProfile, sideOfLegacy, type Profile, type ProfileBook, type ProfileSide } from "./profile.js";

/**
 * Sincroniza la carrera con la cuenta del server.
 *
 * ## Quien manda
 *
 * El CLIENTE. El server guarda la carrera como texto y no la interpreta: toda
 * la logica de carrera (logros, rachas y resumen semanal personal) vive aqui, y
 * duplicarla en el server seria dos implementaciones que se van a separar.
 *
 * **Esto NO es anticheat, pero tampoco alimenta un ranking global.** Un cliente
 * modificado puede falsear SU ficha personal. Una futura clasificacion entre
 * jugadores debera usar resultados autoritativos guardados aparte por el
 * servidor; nunca este perfil sincronizado.
 *
 * ## Que gana al fusionar
 *
 * La que tenga MAS partidos jugados. Es la regla mas simple que no pierde
 * progreso: si vienes de otro dispositivo con mas historia, esa gana; si has
 * jugado aqui sin conexion, la local gana y se sube. En empate se queda la
 * local, para no dar tumbos entre dos iguales.
 */
export interface ProfileSync {
  /** Mensaje del server. Devuelve true si adopto una carrera de fuera. */
  handle(msg: ServerMessage): boolean;
  /** Sube la carrera actual. Se llama al terminar cada partido. */
  /** Sube un lado, o los dos si no se dice cual. */
  push(side?: ProfileSide): void;
  dispose(): void;
}

type Sender = (msg: ClientMessage) => void;

/**
 * Valida lo que baja del server antes de tocar la carrera.
 *
 * Un JSON roto o de otra version no puede tumbar la partida: si no cuadra, se
 * ignora y se sigue con la local.
 */
export function parseProfile(raw: string | null): Profile | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const p = parsed as Partial<Profile>;
  if (typeof p.version !== "number") return null;
  if (!p.totals || typeof p.totals.matches !== "number") return null;
  /*
   * Se pasa por la MISMA migracion que la carrera del disco.
   *
   * Es el camino por el que llega una carrera guardada hace meses desde otro
   * aparato, asi que es exactamente donde hace falta: sin esto, quien recupera
   * su cuenta se encuentra con cien partidos y una media de novato.
   */
  return migrateProfile(p);
}

/**
 * Sincroniza los DOS perfiles (online y offline) con la cuenta.
 *
 * El servidor guarda cada uno en su columna. Un perfil de antes de la
 * separacion llega sin `side` en la columna online, y se reparte con la misma
 * regla que en local (`sideOfLegacy`): si no, un movil recien estrenado
 * metia los partidos contra la IA de toda la vida en el lado online.
 */
export function createProfileSync(book: ProfileBook, send: Sender): ProfileSync {
  let disposed = false;

  const adoptIfBetter = (side: ProfileSide, incoming: Profile | null, force: boolean): boolean => {
    if (!incoming) return false;
    const store = book.of(side);
    if (!force && incoming.totals.matches <= store.get().totals.matches) return false;
    store.adopt(incoming);
    return true;
  };

  /** Lo que ha llegado, ya repartido por lados. */
  const incomingSides = (onlineRaw: string | null, offlineRaw: string | null | undefined) => {
    let online = parseProfile(onlineRaw);
    let offline = parseProfile(offlineRaw ?? null);
    if (online && !online.side && sideOfLegacy(online) === "offline") {
      // El perfil de siempre era de contra la IA: va al lado offline.
      if (!offline) offline = online;
      online = null;
    }
    return { online, offline };
  };

  const pushSide = (side: ProfileSide): void => {
    send({ type: "profile:push", profile: JSON.stringify(book.of(side).get()), side });
  };

  return {
    handle(msg) {
      if (disposed) return false;
      if (msg.type === "welcome") {
        const got = incomingSides(msg.profile, msg.profileOffline);
        let adopted = false;
        for (const side of ["online", "offline"] as const) {
          if (adoptIfBetter(side, got[side], false)) adopted = true;
          // Si la local gana (o no habia nada), se sube: el server tiene que
          // acabar con la buena.
          else if (book.of(side).get().totals.matches > 0) pushSide(side);
        }
        return adopted;
      }
      if (msg.type === "account:redeemed") {
        // Canje explicito: el jugador ha pedido cambiarse a esa cuenta, asi que
        // gana SIEMPRE aunque tenga menos partidos. Es lo que ha pedido.
        const got = incomingSides(msg.profile, msg.profileOffline);
        const a = adoptIfBetter("online", got.online, true);
        const b = adoptIfBetter("offline", got.offline, true);
        return a || b;
      }
      return false;
    },

    push(side?: ProfileSide) {
      if (disposed) return;
      if (side) pushSide(side);
      else {
        pushSide("online");
        pushSide("offline");
      }
    },

    dispose() {
      disposed = true;
    },
  };
}
